import React, { lazy, Suspense } from 'react'
import { FirstRunGuide } from './components/onboarding/FirstRunGuide'
import { FirstRunGuideProvider } from './contexts/FirstRunGuideContext'
import { BrowserRouter as Router, Routes, Route, Navigate, useLocation } from 'react-router-dom'
import { AnimatePresence } from 'framer-motion'
import { PageTransition } from './components/ui/PageTransition'
import { isSupabaseConfigured, missingSupabaseVars } from './lib/supabase'
import { logger } from './lib/productionLogger'
import ErrorBoundary from './components/ErrorBoundary'
import { SafeErrorBoundary } from './components/system/SafeErrorBoundary'
import { RouteDebug } from './components/system/RouteDebug'
import ErrorBanner from './components/ErrorBanner'
import ProtectedRoute from './components/ProtectedRoute'
import Layout from './components/Layout'
import { AuthProvider } from './contexts/AuthContext'
import { ProfileProvider } from './contexts/ProfileContext'
import { TenantProvider } from './contexts/TenantContext'
import { WorkspaceAccessProvider } from './contexts/WorkspaceAccessContext'
import { CreateEmployeeProvider } from './contexts/CreateEmployeeContext'
import { ScrollRestoration } from './lib/router/scroll'
import { isPublicPath } from './lib/routes/publicRoutes'
import { LEAVE_ENABLED } from './flags'
import { AppRouteSuspenseFallback } from './components/system/CalmLoadingShell'

const AdminDashboard = lazy(() => import('./components/admin/DashboardRouter').then(m => ({ default: m.DashboardRouter })))
const CommandCentre = lazy(() => import('./components/admin/CommandCentre'))
const EmployeeDetail = lazy(() => import('./pages/EmployeeDetail'))
const Settings = lazy(() => import('./pages/settings/Settings'))
const InvitationAcceptPage = lazy(() => import('./components/InvitationAcceptPage'))
const PortalAccept = lazy(() => import('./components/portal/PortalAccept'))
const TeamPage = lazy(() => import('./features/team/TeamPage'))
const InternalTeamPage = lazy(() => import('./pages/admin/InternalTeamPage'))
const BlueprintsPage = lazy(() => import('./pages/admin/BlueprintsPage'))
const OrganisationPage = lazy(() => import('./pages/Organisation'))
const BlueprintEditor = lazy(() => import('./pages/admin/BlueprintEditor'))
const MyWorkPage = lazy(() => import('./pages/admin/MyWorkPage'))
const AiOperationsPage = lazy(() => import('./pages/admin/AiOperationsPage'))
const PrivacyPolicy = lazy(() => import('./components/legal/PrivacyPolicy'))
const TermsOfService = lazy(() => import('./components/legal/TermsOfService'))
const HealthCheck = lazy(() => import('./components/HealthCheck'))
const ReportsPage = lazy(() => import('./components/admin/ReportsPage'))
const CompliancePage = lazy(() => import('./components/admin/CompliancePage'))
const RenewalCentre  = lazy(() => import('./pages/admin/RenewalCentre'))
const TasksRoute = lazy(() => import('./features/tasks/TasksRoute'))
const InternalMyTasksPage = lazy(() => import('./pages/admin/InternalMyTasksPage'))
const DocumentsPage = lazy(() => import('./pages/admin/DocumentsPage').then(m => ({ default: m.DocumentsPage })))
const FormsPage = lazy(() => import('./pages/admin/FormsPage').then(m => ({ default: m.FormsPage })))
const ResetPasswordPage = lazy(() => import('./pages/ResetPasswordPage'))
const TimeOffSettingsPage = lazy(() => import('./pages/admin/TimeOffSettingsPage').then(m => ({ default: m.TimeOffSettingsPage })))
const PortalRouter = lazy(() => import('./components/portal/PortalRouter').then(m => ({ default: m.PortalRouter })))
const AdminSignIn = lazy(() => import('./pages/admin/AdminSignIn'))
const AdminSignUp = lazy(() => import('./pages/admin/AdminSignUp'))
const AdminCallback = lazy(() => import('./pages/admin/AdminCallback'))
const AcceptInvitePage = lazy(() => import('./pages/admin/AcceptInvitePage'))
const WelcomeWizard = lazy(() => import('./pages/admin/welcome/index'))
const WelcomeTemplate = lazy(() => import('./pages/admin/welcome/template/index'))
const WelcomeEmployee = lazy(() => import('./pages/admin/welcome/employee/index'))
const WelcomeDone = lazy(() => import('./pages/admin/welcome/done/index'))
const CheckEmail = lazy(() => import('./pages/admin/check-email/index'))
const ConfirmedPage = lazy(() => import('./pages/admin/confirmed/index'))
const PortalSignIn = lazy(() => import('./pages/portal/PortalSignIn'))
const PortalCallback = lazy(() => import('./pages/portal/PortalCallback'))
const PulsePage = lazy(() => import('./pages/portal/PulsePage').then(m => ({ default: m.PulsePage })))
const PublicCertificatePage = lazy(() => import('./pages/PublicCertificatePage').then(m => ({ default: m.PublicCertificatePage })))
const PricingPage = lazy(() => import('./pages/PricingPage').then(m => ({ default: m.PricingPage })))
const AuthAwareRoot = lazy(() => import('./components/AuthAwareRoot').then(m => ({ default: m.AuthAwareRoot })))
const SetupWizard    = lazy(() => import('./pages/admin/SetupWizard'))
const ActivationFlow = lazy(() => import('./pages/admin/ActivationFlow'))
const DesignPreview = lazy(() => import('./components/system/DesignPreview').then(m => ({ default: m.DesignPreview })))
const OverviewPreview = lazy(() => import('./pages/dev/OverviewPreview'))
const OrganisationPreview = lazy(() => import('./pages/dev/OrganisationPreview'))
const OrgPosturePage = lazy(() => import('./pages/admin/OrgPosturePage').then(m => ({ default: m.OrgPosturePage })))
const WorkspaceLayout    = lazy(() => import('./pages/workspace/WorkspaceLayout'))
const WorkspaceHome      = lazy(() => import('./pages/workspace/WorkspaceHome'))
const WorkspaceTasks     = lazy(() => import('./pages/workspace/WorkspaceTasks'))
const WorkspaceEmployees = lazy(() => import('./pages/workspace/WorkspaceEmployees'))
const WorkspaceHistory   = lazy(() => import('./pages/workspace/WorkspaceHistory'))

function AppContent() {
  const location = useLocation()

  logger.log('[App] Current pathname:', location.pathname)
  logger.log('[App] Is public path:', isPublicPath(location.pathname))

  const isPublic = isPublicPath(location.pathname);

  // Public routes - no auth required
  if (isPublic) {
    return (
      <Suspense fallback={<AppRouteSuspenseFallback />}>
      <Routes>
        <Route path="/portal/sign-in" element={<PortalSignIn />} />
        <Route path="/portal/callback" element={<PortalCallback />} />
        <Route path="/portal/pulse/:token" element={<PulsePage />} />
        <Route path="/admin/sign-in" element={<AdminSignIn />} />
        <Route path="/admin/sign-up" element={<AdminSignUp />} />
        <Route path="/admin/callback" element={<AdminCallback />} />
        <Route path="/admin/accept-invite" element={<AcceptInvitePage />} />
        <Route path="/admin/check-email" element={<CheckEmail />} />
        <Route path="/admin/confirmed" element={<ConfirmedPage />} />
        <Route path="/admin/welcome" element={<WelcomeWizard />} />
        <Route path="/admin/welcome/template" element={<WelcomeTemplate />} />
        <Route path="/admin/welcome/employee" element={<WelcomeEmployee />} />
        <Route path="/admin/welcome/done" element={<WelcomeDone />} />
        <Route path="/invitation-accept" element={<InvitationAcceptPage />} />
        <Route path="/portal/accept" element={<PortalAccept />} />
        <Route path="/pricing" element={<PricingPage />} />
        <Route path="/privacy" element={<PrivacyPolicy />} />
        <Route path="/terms" element={<TermsOfService />} />
        <Route path="/health" element={<HealthCheck />} />
        <Route path="/reset-password" element={<ResetPasswordPage />} />
        <Route path="/share/:slug" element={<PublicCertificatePage />} />
        <Route path="/dev/design-preview" element={<DesignPreview />} />
        <Route path="/dev/overview-preview" element={<OverviewPreview />} />
        {import.meta.env.DEV && <Route path="/dev/organisation-preview" element={<OrganisationPreview />} />}
        <Route path="/dev/ping" element={
          <div style={{padding: 24, fontFamily: 'system-ui'}}>
            <h1 style={{fontSize: 24, fontWeight: 600}}>pong</h1>
            <p style={{marginTop: 8, opacity: 0.7}}>Health check OK</p>
          </div>
        } />
      </Routes>
      </Suspense>
    );
  }

  // Protected routes - auth handled by useAuth + TenantProvider + ProtectedRoute
  return (
    <>
      <FirstRunGuide />
      <Layout>
        <Suspense fallback={<AppRouteSuspenseFallback />}>
          <AnimatePresence mode="wait" initial={false}>
          <PageTransition animationKey={location.pathname}>
          <Routes>
            <Route
              path="/dashboard"
              element={
                <ProtectedRoute requiredRole="admin">
                  <CommandCentre />
                </ProtectedRoute>
              }
            />

            <Route
              path="/admin/activate"
              element={
                <ProtectedRoute requiredRole="admin">
                  <ActivationFlow />
                </ProtectedRoute>
              }
            />

            <Route
              path="/admin/setup"
              element={
                <ProtectedRoute requiredRole="admin">
                  <SetupWizard />
                </ProtectedRoute>
              }
            />

            <Route
              path="/setup"
              element={
                <ProtectedRoute requiredRole="admin">
                  <SetupWizard />
                </ProtectedRoute>
              }
            />

            {/* Leave configuration.
                Deliberately carries NO requiredRole. Authority here is not a
                workspace role: an explicitly granted leave overseer may hold
                'employee', and a role gate would bounce the very person the
                grant exists for. ProtectedRoute still requires a signed-in
                user with a tenant; who may configure is answered by
                leave_can_configure() inside the page, which is the only answer
                the database will stand behind — and anyone else gets a stated
                refusal rather than a silent redirect. */}
            <Route
              path="/time-off"
              element={
                <ProtectedRoute>
                  {/* Off means unreachable, not merely unlinked. */}
                  {LEAVE_ENABLED ? <TimeOffSettingsPage /> : <Navigate to="/dashboard" replace />}
                </ProtectedRoute>
              }
            />

            <Route
              path="/settings"
              element={
                <ProtectedRoute requiredRole="admin">
                  <Settings />
                </ProtectedRoute>
              }
            />

            <Route
              path="/reports"
              element={
                <ProtectedRoute requiredRole="admin">
                  <ReportsPage />
                </ProtectedRoute>
              }
            />

            <Route
              path="/compliance"
              element={
                <ProtectedRoute requiredRole="admin">
                  <CompliancePage />
                </ProtectedRoute>
              }
            />

            <Route
              path="/renewals"
              element={
                <ProtectedRoute requiredRole="admin">
                  <RenewalCentre />
                </ProtectedRoute>
              }
            />

            <Route
              path="/tasks"
              element={
                <ProtectedRoute requiredRole="admin">
                  <TasksRoute />
                </ProtectedRoute>
              }
            />
            {/* Legacy duplicate — redirect to canonical /tasks */}
            <Route path="/admin/tasks" element={<Navigate to="/tasks" replace />} />

            <Route
              path="/admin/my-tasks"
              element={
                <ProtectedRoute requiredRole="admin">
                  <InternalMyTasksPage />
                </ProtectedRoute>
              }
            />

            <Route
              path="/admin/documents"
              element={
                <ProtectedRoute requiredRole="admin">
                  <DocumentsPage />
                </ProtectedRoute>
              }
            />

            <Route
              path="/admin/forms"
              element={
                <ProtectedRoute requiredRole="admin">
                  <FormsPage />
                </ProtectedRoute>
              }
            />

            {/* Blueprints (v2 orchestration) — the canonical authoring surface */}
            <Route
              path="/blueprints"
              element={
                <ProtectedRoute requiredRole="admin">
                  <BlueprintsPage />
                </ProtectedRoute>
              }
            />
            <Route
              path="/blueprints/:id"
              element={
                <ProtectedRoute requiredRole="admin">
                  <BlueprintEditor />
                </ProtectedRoute>
              }
            />

            {/* My Work — the contributor's cross-hire worklist (new model) */}
            <Route
              path="/my-work"
              element={
                <ProtectedRoute requiredRole="admin">
                  <MyWorkPage />
                </ProtectedRoute>
              }
            />

            {/* AI Operations — full-page onboard surface */}
            <Route
              path="/operations"
              element={
                <ProtectedRoute requiredRole="admin">
                  <AiOperationsPage />
                </ProtectedRoute>
              }
            />

            {/* Org Posture — CEO 30-second screen */}
            <Route
              path="/posture"
              element={
                <ProtectedRoute requiredRole="admin">
                  <OrgPosturePage />
                </ProtectedRoute>
              }
            />

            {/* Legacy journey-template routes → redirect to Blueprints */}
            <Route path="/templates" element={<Navigate to="/blueprints" replace />} />
            <Route path="/admin/journey-templates" element={<Navigate to="/blueprints" replace />} />
            <Route path="/admin/journey-templates/:id" element={<Navigate to="/blueprints" replace />} />

            <Route
              path="/portal/*"
              element={
                <ProtectedRoute requiredRole="portal">
                  <PortalRouter />
                </ProtectedRoute>
              }
            />

            <Route
              path="/workspace"
              element={
                <ProtectedRoute requiredRole="internal">
                  <WorkspaceLayout />
                </ProtectedRoute>
              }
            >
              <Route index element={<WorkspaceHome />} />
              <Route path="tasks"     element={<WorkspaceTasks />} />
              <Route path="employees" element={<WorkspaceEmployees />} />
              <Route path="history"   element={<WorkspaceHistory />} />
            </Route>

            <Route
              path="/my-tasks"
              element={<Navigate to="/portal" replace />}
            />

            {/* Canonical people routes */}
            <Route
              path="/people/:employeeId"
              element={
                <ProtectedRoute requiredRole="admin">
                  <EmployeeDetail />
                </ProtectedRoute>
              }
            />
            <Route
              path="/people"
              element={
                <ProtectedRoute requiredRole="admin">
                  <AdminDashboard />
                </ProtectedRoute>
              }
            />
            <Route
              path="/organisation"
              element={
                <ProtectedRoute requiredRole="admin">
                  <OrganisationPage />
                </ProtectedRoute>
              }
            />

            {/* Legacy /team URLs → permanent redirect to canonical /people */}
            <Route path="/team/:employeeId" element={<Navigate to="/people/:employeeId" replace />} />
            <Route path="/team"             element={<Navigate to="/people"             replace />} />

            <Route
              path="/employee"
              element={<Navigate to="/portal" replace />}
            />

            <Route
              path="/onboard"
              element={<Navigate to="/people?action=add" replace />}
            />

            <Route
              path="/admin/team-members"
              element={
                <ProtectedRoute requiredRole="admin">
                  <InternalTeamPage />
                </ProtectedRoute>
              }
            />

            <Route
              path="/"
              element={<AuthAwareRoot />}
            />

            <Route
              path="*"
              element={<AuthAwareRoot />}
            />
          </Routes>
          </PageTransition>
          </AnimatePresence>
        </Suspense>
      </Layout>
    </>
  )
}

function App() {
  if (!isSupabaseConfigured) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center p-4">
        <div className="max-w-md w-full">
          <ErrorBanner
            message={`Missing required environment variables: ${missingSupabaseVars.join(', ')}`}
            variant="error"
          />
          <div className="mt-4 p-4 bg-white rounded-lg border border-gray-200">
            <h3 className="font-semibold text-gray-900 mb-2">Setup Required</h3>
            <p className="text-sm text-gray-600">
              Please configure your Supabase environment variables to continue.
            </p>
          </div>
        </div>
      </div>
    )
  }

  return (
    <SafeErrorBoundary>
      <ErrorBoundary>
        <Router>
          <ScrollRestoration />
          <AuthProvider>
            <TenantProvider>
              <ProfileProvider>
                {/* WorkspaceAccessProvider fetches billing status once per session.
                    Must be inside TenantProvider (needs tenantId). */}
                <WorkspaceAccessProvider>
                  <CreateEmployeeProvider>
                    <FirstRunGuideProvider>
                      <RouteDebug />
                      <AppContent />
                    </FirstRunGuideProvider>
                  </CreateEmployeeProvider>
                </WorkspaceAccessProvider>
              </ProfileProvider>
            </TenantProvider>
          </AuthProvider>
        </Router>
      </ErrorBoundary>
    </SafeErrorBoundary>
  )
}

export default App
